/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Skill Content Tabs Component - Tabbed interface for focused practice content
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  BookOpen,
  Lightbulb,
  AlertTriangle,
  Eye,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Image as ImageIcon,
  GitBranch,
  ExternalLink
} from 'lucide-react'
import { renderContent } from '../utils/contentRenderer'
import ConceptIllustrationFigure, { illustrationKey } from './ConceptIllustrationFigure'
import {
  getAnswerOptionContent,
  getOptionLabel,
  getOptionText,
  isOptionMatch
} from '../utils/answerUtils'

// Safe wrapper for rendering option text - always sanitizes
const renderOptionContent = (option, contentType) => {
  const text = getOptionText(option)
  if (!text) return null
  return renderContent(text, contentType)
}

// Validate an alternative-solution Desmos link before rendering it as an href. A JSX href does NOT
// pass through the HTML sanitizer, so a javascript:/data: URL would execute. Require an https(s) URL
// on a desmos.com host (the link is labeled "Open in Desmos"); otherwise drop the link.
const safeDesmosUrl = (url) => {
  if (typeof url !== 'string') return null
  try {
    const u = new URL(url)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
    if (u.hostname !== 'desmos.com' && !u.hostname.endsWith('.desmos.com')) return null
    return u.href
  } catch {
    return null
  }
}

const tabs = [
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'examples', label: 'Examples', icon: Lightbulb },
  { id: 'watchout', label: 'Watch Out', icon: AlertTriangle },
  { id: 'review', label: 'Review', icon: Eye }
]

const SkillContentTabs = ({
  skill = null,
  expandedQuestions,
  toggleQuestion,
  reviewedIds,
  getAnswerLabel,
  citable = false
}) => {
  const [activeTab, setActiveTab] = useState('overview')
  const [renderError, setRenderError] = useState(null)

  const hasExamples = skill?.examples?.length > 0
  const hasMistakes = skill?.commonMistakes?.length > 0
  const hasQuestions = skill?.wrongQuestions?.length > 0

  // Count content for badges
  const questionCount = skill?.wrongQuestions?.length || 0

  // Only show tabs that have content (Overview always). A data-less tab (e.g. Review with no wrong
  // questions, as in the educator node panel) is hidden rather than shown disabled.
  const visibleTabs = tabs.filter((tab) => {
    if (tab.id === 'examples') return hasExamples
    if (tab.id === 'watchout') return hasMistakes
    if (tab.id === 'review') return hasQuestions
    return true
  })

  // Safe tab change handler
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId)
    setRenderError(null)
  }, [])

  // Error boundary fallback
  if (renderError) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700 dark:text-red-300">Failed to render skill content</p>
        <button
          onClick={() => setRenderError(null)}
          className="mt-2 px-4 py-2 text-sm bg-red-100 dark:bg-red-900/30 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50"
        >
          Try Again
        </button>
      </div>
    )
  }

  // Guard against missing skill data
  if (!skill) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center">
        <p className="text-gray-500 dark:text-gray-400">No skill data available</p>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Skill Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            <BookOpen
              className="w-5 h-5 text-primary-600 dark:text-primary-400"
              aria-hidden="true"
            />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {skill.skillName || 'Skill'}
          </h3>
        </div>
      </div>

      {/* Tab Navigation */}
      <div
        className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
        role="tablist"
        aria-label="Skill content tabs"
      >
        {visibleTabs.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id

          return (
            <button
              key={tab.id}
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => handleTabChange(tab.id)}
              className={`
                flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium
                transition-all border-b-2 -mb-[1px]
                ${
                  isActive
                    ? 'text-primary-600 dark:text-primary-400 border-primary-600 dark:border-primary-400 bg-white dark:bg-gray-800'
                    : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                }
              `}
            >
              <Icon className="w-4 h-4" aria-hidden="true" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.id === 'review' && questionCount > 0 && (
                <span
                  className={`
                  px-1.5 py-0.5 text-xs rounded-full
                  ${
                    isActive
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }
                `}
                  aria-label={`${questionCount} questions to review`}
                >
                  {questionCount}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <div className="p-5">
        {/* Overview Tab */}
        <div
          role="tabpanel"
          id="tabpanel-overview"
          aria-labelledby="tab-overview"
          hidden={activeTab !== 'overview'}
        >
          {activeTab === 'overview' && (
            <div className="space-y-5">
              {/* Summary */}
              {skill.summary && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Summary
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {skill.summary}
                  </p>
                </div>
              )}

              {/* Detailed Explanation */}
              {skill.detailedExplanation && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Detailed Explanation
                  </h4>
                  <div className="force-light-mode bg-white rounded-lg p-3">
                    <div className="prose prose-sm max-w-none text-gray-700">
                      {renderContent(skill.detailedExplanation, 'md')}
                    </div>
                  </div>
                </div>
              )}

              {/* Key Concepts */}
              {skill.concepts?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Key Concepts
                  </h4>
                  <div className="grid gap-3">
                    {skill.concepts.map((concept, i) => (
                      <div
                        key={i}
                        className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-100 dark:border-primary-800"
                      >
                        <p className="font-semibold text-primary-900 dark:text-primary-200 mb-1">
                          {concept.name}
                        </p>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          {renderContent(concept.definition, 'md')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Memory Tips */}
              {skill.memoryTips?.length > 0 && (
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
                  <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-200 mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" aria-hidden="true" />
                    Memory Tips
                  </h4>
                  <ul className="space-y-2">
                    {skill.memoryTips.map((tip, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-purple-700 dark:text-purple-300"
                      >
                        <span className="text-purple-400 mt-1" aria-hidden="true">
                          •
                        </span>
                        <div className="min-w-0">{renderContent(tip, 'md')}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Why it's tricky (schema v2 DOK guidance) */}
              {skill.dokGuidance &&
                (skill.dokGuidance.definition || skill.dokGuidance.mustRequire?.length > 0) && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800">
                    <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-2">
                      Why it&apos;s tricky
                    </h4>
                    {skill.dokGuidance.definition && (
                      <div className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                        {renderContent(skill.dokGuidance.definition, 'md')}
                      </div>
                    )}
                    {skill.dokGuidance.mustRequire?.length > 0 && (
                      <ul className="space-y-1">
                        {skill.dokGuidance.mustRequire.map((m, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300"
                          >
                            <span className="mt-1" aria-hidden="true">
                              •
                            </span>
                            <div className="min-w-0">{renderContent(m, 'md')}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

              {/* Key theorems (schema v2 — name + definition only, no citations for students) */}
              {skill.theorems?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Key Theorems
                  </h4>
                  <div className="grid gap-3">
                    {skill.theorems.map((t, i) => (
                      <div
                        key={t.name || i}
                        className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700"
                      >
                        <p className="font-semibold text-gray-900 dark:text-gray-200 mb-1">
                          {t.name}
                        </p>
                        {t.definition && (
                          <div className="text-sm text-gray-700 dark:text-gray-300">
                            {renderContent(t.definition, 'md')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Real-world connection (schema v2) */}
              {skill.realWorldConnection && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Real-World Connection
                  </h4>
                  <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {renderContent(skill.realWorldConnection, 'md')}
                  </div>
                </div>
              )}

              {/* Visual concepts (schema v2.3.0 concept illustrations — self-contained, grayscale-safe SVG) */}
              {skill.conceptIllustrations?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" aria-hidden="true" />
                    Visual Concepts
                  </h4>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {skill.conceptIllustrations.map((il, i) => (
                      // `citable` views (admin/educator) also show the stable id for citing.
                      <ConceptIllustrationFigure
                        key={illustrationKey(il, i)}
                        il={il}
                        index={i}
                        showId={citable}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Alternative approaches (schema v2.3.0 alternative_solutions — $...$ LaTeX walkthroughs) */}
              {skill.alternativeSolutions?.length > 0 && (
                <div id="node-alternative-approaches" className="scroll-mt-4">
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <GitBranch className="w-4 h-4" aria-hidden="true" />
                    Alternative Approaches
                  </h4>
                  <div className="space-y-3">
                    {skill.alternativeSolutions.map((sol, i) => {
                      const desmosUrl = safeDesmosUrl(sol.desmosUrl)
                      return (
                        <div
                          key={i}
                          className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800"
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <p className="font-semibold text-indigo-900 dark:text-indigo-200">
                              {sol.approach}
                            </p>
                            {desmosUrl && (
                              <a
                                href={desmosUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                              >
                                <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                                Open in Desmos
                              </a>
                            )}
                          </div>
                          {sol.whenToUse && (
                            <p className="text-xs text-indigo-700 dark:text-indigo-300 italic mb-2">
                              {sol.whenToUse}
                            </p>
                          )}
                          {sol.walkthrough && (
                            <div className="force-light-mode bg-white rounded-lg p-3">
                              <div className="prose prose-sm max-w-none text-gray-700">
                                {renderContent(sol.walkthrough, 'md')}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {!skill.summary &&
                !skill.detailedExplanation &&
                !skill.concepts?.length &&
                !skill.theorems?.length &&
                !skill.dokGuidance &&
                !skill.realWorldConnection &&
                !skill.conceptIllustrations?.length &&
                !skill.alternativeSolutions?.length && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No overview content available for this skill.
                  </p>
                )}
            </div>
          )}
        </div>

        {/* Examples Tab */}
        <div
          role="tabpanel"
          id="tabpanel-examples"
          aria-labelledby="tab-examples"
          hidden={activeTab !== 'examples'}
        >
          {activeTab === 'examples' && (
            <div className="space-y-4">
              {skill.examples?.length > 0 ? (
                skill.examples.map((ex, i) => (
                  <div
                    key={i}
                    className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800"
                  >
                    <h5 className="font-semibold text-green-900 dark:text-green-200 mb-2">
                      {ex.title || `Example ${i + 1}`}
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          Problem:{' '}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300">{ex.problem}</span>
                      </div>
                      <div>
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          Solution:{' '}
                        </span>
                        <span className="text-gray-700 dark:text-gray-300">{ex.solution}</span>
                      </div>
                      {ex.keyInsight && (
                        <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700">
                          <span className="text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                            <Lightbulb className="w-3 h-3" aria-hidden="true" /> Key Insight:
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 italic ml-4 block mt-1">
                            {ex.keyInsight}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  No examples available for this skill.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Watch Out Tab */}
        <div
          role="tabpanel"
          id="tabpanel-watchout"
          aria-labelledby="tab-watchout"
          hidden={activeTab !== 'watchout'}
        >
          {activeTab === 'watchout' && (
            <div className="space-y-4">
              {skill.commonMistakes?.length > 0 ? (
                skill.commonMistakes.map((m, i) => (
                  <div
                    key={i}
                    className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800"
                  >
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle
                          className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0"
                          aria-hidden="true"
                        />
                        <div>
                          <span className="text-red-700 dark:text-red-400 font-medium">
                            Common Mistake:{' '}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300">{m.mistake}</span>
                        </div>
                      </div>
                      <div className="pl-6">
                        <span className="text-amber-700 dark:text-amber-400 font-medium">
                          Why it&apos;s wrong:{' '}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400 text-sm">
                          {m.whyWrong}
                        </span>
                      </div>
                      <div className="pl-6 flex items-start gap-2">
                        <CheckCircle2
                          className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0"
                          aria-hidden="true"
                        />
                        <div>
                          <span className="text-green-700 dark:text-green-400 font-medium">
                            How to avoid:{' '}
                          </span>
                          <span className="text-gray-700 dark:text-gray-300 text-sm">
                            {m.howToAvoid}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  No common mistakes documented for this skill.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Review Tab */}
        <div
          role="tabpanel"
          id="tabpanel-review"
          aria-labelledby="tab-review"
          hidden={activeTab !== 'review'}
        >
          {activeTab === 'review' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Review the questions you missed. Click each to see the full question and
                explanation.
              </p>
              {skill.wrongQuestions?.length > 0 ? (
                skill.wrongQuestions.map((question, qIdx) => {
                  const userAnswerLabel = getAnswerLabel(question.userAnswer)
                  const correctAnswerLabel = getAnswerLabel(question.correctAnswer)
                  const userAnswerContent = getAnswerOptionContent(
                    question.answerOptions,
                    userAnswerLabel
                  )
                  const correctAnswerContent = getAnswerOptionContent(
                    question.answerOptions,
                    correctAnswerLabel
                  )
                  const isExpanded = expandedQuestions[question.questionId]
                  const questionPanelId = `question-panel-${question.questionId}`

                  return (
                    <div
                      key={question.questionId}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                    >
                      <button
                        onClick={() => toggleQuestion(question.questionId)}
                        aria-expanded={isExpanded}
                        aria-controls={questionPanelId}
                        className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className="w-8 h-8 flex items-center justify-center bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-sm font-semibold"
                            aria-hidden="true"
                          >
                            {qIdx + 1}
                          </span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            Question {qIdx + 1}
                          </span>
                          {reviewedIds.includes(question.questionId) && (
                            <CheckCircle2
                              className="h-4 w-4 text-green-500"
                              aria-label="Reviewed"
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-red-600 dark:text-red-400">
                              Your: {userAnswerLabel}
                            </span>
                            <span className="text-gray-400" aria-hidden="true">
                              /
                            </span>
                            <span className="text-green-600 dark:text-green-400">
                              Correct: {correctAnswerLabel}
                            </span>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          )}
                        </div>
                      </button>

                      {isExpanded && (
                        <div
                          id={questionPanelId}
                          className="force-light-mode px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50"
                        >
                          <div className="space-y-4">
                            {/* Question Text */}
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                Full Question
                              </p>
                              <div className="bg-white rounded-lg p-3">
                                <div className="prose prose-sm max-w-none text-gray-900">
                                  {renderContent(question.questionText, question.contentType)}
                                </div>
                              </div>
                            </div>

                            {/* Answer Options - Show all options with correct/incorrect highlighting */}
                            {question.answerOptions &&
                              Array.isArray(question.answerOptions) &&
                              question.answerOptions.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                    Answer Choices
                                  </p>
                                  <div
                                    className="space-y-2"
                                    role="list"
                                    aria-label="Answer options"
                                  >
                                    {question.answerOptions.map((option, optIdx) => {
                                      const optionLabel = getOptionLabel(option, optIdx)
                                      const isCorrect = isOptionMatch(
                                        option,
                                        optIdx,
                                        correctAnswerLabel
                                      )
                                      const isUserAnswer = isOptionMatch(
                                        option,
                                        optIdx,
                                        userAnswerLabel
                                      )

                                      return (
                                        <div
                                          key={optIdx}
                                          role="listitem"
                                          className={`p-3 rounded-lg flex items-start gap-3 ${
                                            isCorrect
                                              ? 'bg-green-100 border-2 border-green-400'
                                              : isUserAnswer
                                                ? 'bg-red-100 border-2 border-red-400'
                                                : 'bg-white border border-gray-200'
                                          }`}
                                        >
                                          <span
                                            className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                                              isCorrect
                                                ? 'bg-green-500 text-white'
                                                : isUserAnswer
                                                  ? 'bg-red-500 text-white'
                                                  : 'bg-gray-200 text-gray-600'
                                            }`}
                                            aria-hidden="true"
                                          >
                                            {optionLabel}
                                          </span>
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm text-gray-900">
                                              {renderOptionContent(option, question.contentType)}
                                            </div>
                                            {(isCorrect || isUserAnswer) && (
                                              <div className="mt-1 flex items-center gap-1.5">
                                                {isCorrect && (
                                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                                    <CheckCircle2
                                                      className="w-3.5 h-3.5"
                                                      aria-hidden="true"
                                                    />
                                                    Correct Answer
                                                  </span>
                                                )}
                                                {isUserAnswer && !isCorrect && (
                                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
                                                    <XCircle
                                                      className="w-3.5 h-3.5"
                                                      aria-hidden="true"
                                                    />
                                                    Your Answer
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              )}

                            {/* Fallback: Simple answer display if no answerOptions */}
                            {(!question.answerOptions ||
                              !Array.isArray(question.answerOptions) ||
                              question.answerOptions.length === 0) && (
                              <div className="grid grid-cols-2 gap-4">
                                <div className="p-3 bg-red-100 rounded-lg border-2 border-red-300">
                                  <p className="text-xs font-medium text-gray-600 mb-1">
                                    Your Answer
                                  </p>
                                  <p className="text-lg font-bold text-red-700">
                                    {userAnswerLabel}
                                  </p>
                                  {userAnswerContent && (
                                    <div className="text-sm text-gray-700 mt-1">
                                      {renderContent(userAnswerContent, question.contentType)}
                                    </div>
                                  )}
                                </div>
                                <div className="p-3 bg-green-100 rounded-lg border-2 border-green-300">
                                  <p className="text-xs font-medium text-gray-600 mb-1">
                                    Correct Answer
                                  </p>
                                  <p className="text-lg font-bold text-green-700">
                                    {correctAnswerLabel}
                                  </p>
                                  {correctAnswerContent && (
                                    <div className="text-sm text-gray-700 mt-1">
                                      {renderContent(correctAnswerContent, question.contentType)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Rationale */}
                            {question.rationale && (
                              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
                                  Explanation
                                </p>
                                <div className="bg-white rounded-lg p-3">
                                  <div className="prose prose-sm max-w-none text-gray-700">
                                    {renderContent(question.rationale, question.contentType)}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  No questions to review for this skill.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

SkillContentTabs.propTypes = {
  skill: PropTypes.shape({
    skillName: PropTypes.string,
    summary: PropTypes.string,
    detailedExplanation: PropTypes.string,
    examples: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        problem: PropTypes.string,
        solution: PropTypes.string,
        keyInsight: PropTypes.string
      })
    ),
    commonMistakes: PropTypes.arrayOf(
      PropTypes.shape({
        mistake: PropTypes.string,
        whyWrong: PropTypes.string,
        howToAvoid: PropTypes.string
      })
    ),
    memoryTips: PropTypes.arrayOf(PropTypes.string),
    concepts: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        definition: PropTypes.string
      })
    ),
    conceptIllustrations: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        index: PropTypes.number,
        depicts: PropTypes.string,
        svg: PropTypes.string,
        altText: PropTypes.string,
        dok: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    ),
    alternativeSolutions: PropTypes.arrayOf(
      PropTypes.shape({
        approach: PropTypes.string,
        whenToUse: PropTypes.string,
        walkthrough: PropTypes.string,
        desmosUrl: PropTypes.string
      })
    ),
    wrongQuestions: PropTypes.arrayOf(
      PropTypes.shape({
        questionId: PropTypes.string.isRequired,
        questionText: PropTypes.string,
        contentType: PropTypes.string,
        userAnswer: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        correctAnswer: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        answerOptions: PropTypes.array,
        rationale: PropTypes.string
      })
    )
  }),
  expandedQuestions: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleQuestion: PropTypes.func.isRequired,
  reviewedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  getAnswerLabel: PropTypes.func.isRequired,
  // When true (admin/educator views), each illustration also shows its stable id for citing.
  citable: PropTypes.bool
}

export default SkillContentTabs
